import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { COLORS, FONTS } from '../../constants/config';
import { organizationsApi } from '../../services/apiService';
import { Organization } from '../../types';
import { PageContainer, Navbar, Breadcrumb } from '../../components';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { invalidate as invalidateData } from '../../utils/dataInvalidation';
import Svg, { Path } from 'react-native-svg';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
// Back Icon
const BackIcon = ({ size = 24, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M19 12H5" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 19L5 12L12 5" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

type RouteParams = {
  EditOrganization: {
    organizationId: number;
  };
};

interface FormData {
  name: string;
  vision: string;
  mission: string;
  description: string;
  values: string;
  employees_count: number;
  founding_year: string;
  primary_color: string;
  secondary_color: string;
}

export default function EditOrganizationScreen() {
  const navigation = useNavigation();
  const route = useRoute<RouteProp<RouteParams, 'EditOrganization'>>();
  const { organizationId } = route.params;
  const { showSuccess, showError } = useNotification();
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();

  // Theme colors
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    headerTitle: colors.textPrimary,
    label: colors.textSecondary,
    inputBg: isDark ? colors.backgroundAlt : tokens.colors.raw.gray["50"],
    inputBorder: colors.border,
    inputText: colors.textPrimary,
    loadingText: colors.textMuted,
    cancelBtnBorder: colors.border,
    cancelBtnText: colors.textMuted,
    actionBorder: colors.border,
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    vision: '',
    mission: '',
    description: '',
    values: '',
    employees_count: 0,
    founding_year: '',
    primary_color: '#2563EB',
    secondary_color: '#A6813F',
  });

  const fetchOrganization = useCallback(async () => {
    try {
      setLoading(true);
      const data = await organizationsApi.get(organizationId);
      setOrganization(data);
      setFormData({
        name: data.name || '',
        vision: data.vision || '',
        mission: data.mission || '',
        description: data.description || '',
        values: data.values || '',
        employees_count: data.employees_count || 0,
        founding_year: data.founding_year?.toString() || '',
        primary_color: data.primary_color || '#2563EB',
        secondary_color: data.secondary_color || '#A6813F',
      });
    } catch (error) {
      console.error('Error fetching organization:', error);
      showError(t('messages.error.loadingOrganization'));
    } finally {
      setLoading(false);
    }
  }, [organizationId, showError]);

  useEffect(() => {
    fetchOrganization();
  }, [fetchOrganization]);

  const handleSave = async () => {
    if (!formData.vision.trim()) {
      showError(t('validation.visionRequired'));
      return;
    }
    if (!formData.mission.trim()) {
      showError(t('validation.missionRequired'));
      return;
    }
    if (!formData.description.trim()) {
      showError(t('validation.descriptionRequired'));
      return;
    }
    if (!formData.values.trim()) {
      showError(t('validation.valuesRequired'));
      return;
    }

    try {
      setSaving(true);
      await organizationsApi.update(organizationId, {
        vision: formData.vision.trim(),
        mission: formData.mission.trim(),
        description: formData.description.trim(),
        values: formData.values.trim(),
        employees_count: formData.employees_count || 0,
        founding_year: formData.founding_year ? parseInt(formData.founding_year) : null,
        primary_color: formData.primary_color.trim() || '#2563EB',
        secondary_color: formData.secondary_color.trim() || '#A6813F',
      });
      showSuccess(t('messages.success.organizationUpdated'));
      invalidateData('my-organization');
      navigation.goBack();
    } catch (error: any) {
      console.error('Error updating organization:', error);
      showError(error.response?.data?.detail || t('messages.error.updatingOrganization'));
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: keyof FormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <PageContainer>
        <Navbar />
        <Breadcrumb
          items={[
            { label: t('navigation.home') },
            { label: t('organizations.editOrganization') },
          ]}
        />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={[styles.loadingText, { color: themeColors.loadingText }]}>{t('common.loading')}</Text>
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <Breadcrumb
        items={[
          { label: t('navigation.home') },
          { label: t('organizations.editOrganization') },
        ]}
      />
      <KeyboardAvoidingView
        style={styles.keyboardAvoid}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {/* Header */}
          <View style={styles.header}>
            <TouchableOpacity style={styles.backBtn} onPress={() => navigation.goBack()}>
              <BackIcon size={20} color={colors.iconDefault} />
            </TouchableOpacity>
            <Text style={[styles.headerTitle, { color: themeColors.headerTitle }]}>{t('organizations.editOrganizationData')}</Text>
          </View>

          {/* Form Card */}
          <View style={[styles.formCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder, borderWidth: tokens.spacing.absolute["px"] }]}>
            {/* Vision */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>{t('organizations.vision')} <Text style={styles.required}>*</Text></Text>
              <TextInput
                style={[styles.input, styles.multilineInput]}
                value={formData.vision}
                onChangeText={(text) => updateField('vision', text)}
                placeholder={t('organizations.enterVision')}
                placeholderTextColor={tokens.colors.raw.gray["400"]}
                multiline
                numberOfLines={3}
                textAlignVertical="top"
              />
            </View>

            {/* Mission */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>{t('organizations.mission')} <Text style={styles.required}>*</Text></Text>
              <TextInput
                style={[styles.input, styles.multilineInput]}
                value={formData.mission}
                onChangeText={(text) => updateField('mission', text)}
                placeholder={t('organizations.enterMission')}
                placeholderTextColor={tokens.colors.raw.gray["400"]}
                multiline
                numberOfLines={3}
                textAlignVertical="top"
              />
            </View>

            {/* Description */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>{t('organizations.about')} <Text style={styles.required}>*</Text></Text>
              <TextInput
                style={[styles.input, styles.multilineInput, { minHeight: 120 }]}
                value={formData.description}
                onChangeText={(text) => updateField('description', text)}
                placeholder={t('organizations.enterAbout')}
                placeholderTextColor={tokens.colors.raw.gray["400"]}
                multiline
                numberOfLines={5}
                textAlignVertical="top"
              />
            </View>

            {/* Values */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>{t('organizations.values')} <Text style={styles.required}>*</Text></Text>
              <TextInput
                style={[styles.input, styles.multilineInput]}
                value={formData.values}
                onChangeText={(text) => updateField('values', text)}
                placeholder={t('organizations.enterValues')}
                placeholderTextColor={tokens.colors.raw.gray["400"]}
                multiline
                numberOfLines={2}
                textAlignVertical="top"
              />
            </View>

            {/* Stats Row */}
            <View style={styles.statsRow}>
              {/* Employees Count */}
              <View style={[styles.inputGroup, styles.halfWidth]}>
                <Text style={styles.label}>{t('employees.count')}</Text>
                <TextInput
                  style={styles.input}
                  value={formData.employees_count.toString()}
                  onChangeText={(text) => updateField('employees_count', parseInt(text) || 0)}
                  placeholder="0"
                  placeholderTextColor={tokens.colors.raw.gray["400"]}
                  keyboardType="numeric"
                />
              </View>

              {/* Founding Year */}
              <View style={[styles.inputGroup, styles.halfWidth]}>
                <Text style={styles.label}>{t('organizations.foundingYear')}</Text>
                <TextInput
                  style={styles.input}
                  value={formData.founding_year}
                  onChangeText={(text) => updateField('founding_year', text.replace(/[^0-9]/g, ''))}
                  placeholder={t('organizations.foundingYearExample')}
                  placeholderTextColor={tokens.colors.raw.gray["400"]}
                  keyboardType="numeric"
                  maxLength={4}
                />
              </View>
            </View>

            {/* Org Chart Colors */}
            <View style={styles.statsRow}>
              <View style={[styles.inputGroup, styles.halfWidth]}>
                <Text style={styles.label}>{t('organizations.primaryColor', 'اللون الأساسي للهيكل')}</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: tokens.spacing.absolute["2"] }}>
                  <View style={{ width: 32, height: 32, borderRadius: tokens.radii.absolute["radius-6"], backgroundColor: formData.primary_color, borderWidth: tokens.spacing.absolute["px"], borderColor: tokens.colors.raw.gray["200"] }} />
                  <TextInput
                    style={[styles.input, { flex: 1, fontFamily: 'monospace' }]}
                    value={formData.primary_color}
                    onChangeText={(text) => {
                      const cleaned = text.startsWith('#') ? text : '#' + text;
                      updateField('primary_color', cleaned.slice(0, 7));
                    }}
                    placeholder="#2563EB"
                    placeholderTextColor={tokens.colors.raw.gray["400"]}
                    maxLength={7}
                  />
                </View>
              </View>

              <View style={[styles.inputGroup, styles.halfWidth]}>
                <Text style={styles.label}>{t('organizations.secondaryColor', 'اللون الثانوي للهيكل')}</Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: tokens.spacing.absolute["2"] }}>
                  <View style={{ width: 32, height: 32, borderRadius: tokens.radii.absolute["radius-6"], backgroundColor: formData.secondary_color, borderWidth: tokens.spacing.absolute["px"], borderColor: tokens.colors.raw.gray["200"] }} />
                  <TextInput
                    style={[styles.input, { flex: 1, fontFamily: 'monospace' }]}
                    value={formData.secondary_color}
                    onChangeText={(text) => {
                      const cleaned = text.startsWith('#') ? text : '#' + text;
                      updateField('secondary_color', cleaned.slice(0, 7));
                    }}
                    placeholder="#A6813F"
                    placeholderTextColor={tokens.colors.raw.gray["400"]}
                    maxLength={7}
                  />
                </View>
              </View>
            </View>

            {/* Action Buttons */}
            <View style={styles.actionRow}>
              <Button
                variant="secondary"
                onPress={() => navigation.goBack()}
                disabled={saving}
              >
                {t('common.cancel')}
              </Button>

              <Button
                variant="primary"
                onPress={handleSave}
                loading={saving}
                disabled={saving}
              >
                {t('common.saveChanges')}
              </Button>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  keyboardAvoid: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: tokens.spacing.absolute["6"],
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: tokens.spacing.absolute["3"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: COLORS.textMuted,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: tokens.spacing.absolute["6"],
  },
  backBtn: {
    padding: tokens.spacing.absolute["2"],
    marginStart: tokens.spacing.absolute["3"],
  },
  headerTitle: {
    fontSize: tokens.typography["heading 4"]["font size"],
    fontFamily: FONTS.bold,
    color: tokens.colors.raw.gray["900"],
  },
  formCard: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["6"],
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    elevation: 3,
  },
  inputGroup: {
    marginBottom: tokens.spacing.absolute["5"],
  },
  label: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["700"],
    marginBottom: tokens.spacing.absolute["2"],
    textAlign: getTextAlign(isRTL),
  },
  required: {
    color: tokens.colors.raw.red["500"],
  },
  input: {
    backgroundColor: tokens.colors.raw.gray["50"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["3"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["900"],
    textAlign: getTextAlign(isRTL),
  },
  multilineInput: {
    minHeight: 80,
    paddingTop: tokens.spacing.absolute["3"],
  },
  statsRow: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["4"],
  },
  halfWidth: {
    flex: 1,
  },
  actionRow: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: tokens.spacing.absolute["3"],
    marginTop: tokens.spacing.absolute["6"],
    paddingTop: tokens.spacing.absolute["6"],
    borderTopWidth: tokens.spacing.absolute["px"],
    borderTopColor: tokens.colors.raw.gray["200"],
  },
});

const styles = getStyles(true);
